import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import Topbar from '../../components/topbar';
import AddFeatureFlagModal from '../../components/popup/add-feature-flag';
import DeletePopup from '../../components/popup/delete';
import AddBtn from '../../components/ui/add-btn';
import DynamicAdminTable, { Column } from '../../components/admin-table';
import Pagination from '../../components/pagination';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';

const PAGE_SIZE = 10;

type FlagRow = {
    id: number;
    featureKey: string;
    label: string;
    allowedCountryIds: number[];
    excludedEntityIds: number[];
    allowedCountryNames: string;
    excludedEntityNames: string;
    isActive: boolean;
};

type Country = { id: number; country: string };
type Entity = { id: number; name: string };

const FeatureFlags: React.FC = () => {
    const { showSuccess, showError } = useToast();

    const [data, setData] = React.useState<FlagRow[]>([]);
    const [countries, setCountries] = React.useState<Country[]>([]);
    const [entities, setEntities] = React.useState<Entity[]>([]);
    const [openModal, setOpenModal] = React.useState('');
    const [selectedRow, setSelectedRow] = React.useState<FlagRow | null>(null);
    const [page, setPage] = React.useState(1);

    const [fetchFlags, setFetchFlags] = useApi<any>();
    const [fetchCountries, setFetchCountries] = useApi<any>();
    const [fetchEntities, setFetchEntities] = useApi<any>();
    const [upsertResult, setUpsert] = useApi<any>();
    const [deleteResult, setDelete] = useApi<any>();
    const [toggleResult, setToggle] = useApi<any>();

    useEffect(() => { document.title = 'FineAnts Admin | Feature Flags'; }, []);

    const loadFlags = () => {
        setFetchFlags(buildApiRequestConfig(routes.features.getAll));
    };

    useEffect(() => {
        loadFlags();
        setFetchCountries(buildApiRequestConfig(routes.country.fetch, [{ param: 'showDisabled', value: false }]));
        setFetchEntities(buildApiRequestConfig(routes.company.fetch));
    }, []);

    // Reload after mutations
    useEffect(() => { loadFlags(); }, [upsertResult.data, deleteResult.data, toggleResult.data]);

    useEffect(() => {
        if (!fetchCountries.data) return;
        setCountries((fetchCountries.data as any[]).map((c: any) => ({ id: c.id, country: c.country })));
    }, [fetchCountries.data]);

    useEffect(() => {
        if (!fetchEntities.data) return;
        const list = Array.isArray(fetchEntities.data) ? fetchEntities.data : fetchEntities.data?.entities ?? [];
        // GET /entities (entity-controller.js getEntities) returns the
        // entity's name as `company`, not `name`.
        setEntities(list.map((e: any) => ({ id: e.id, name: e.company ?? e.name ?? `#${e.id}` })));
    }, [fetchEntities.data]);

    useEffect(() => {
        if (fetchFlags.error) { showError('Failed to fetch feature flags'); return; }
        if (!fetchFlags.data?.flags) return;
        const countryMap = new Map(countries.map(c => [c.id, c.country]));
        const entityMap = new Map(entities.map(e => [e.id, e.name]));
        setData((fetchFlags.data.flags as any[]).map((f: any) => ({
            id: f.id,
            featureKey: f.featureKey,
            label: f.label,
            allowedCountryIds: f.allowedCountryIds ?? [],
            excludedEntityIds: f.excludedEntityIds ?? [],
            allowedCountryNames: (f.allowedCountryIds ?? []).length
                ? (f.allowedCountryIds as number[]).map(id => countryMap.get(id) ?? `#${id}`).join(', ')
                : 'All countries',
            excludedEntityNames: (f.excludedEntityIds ?? []).length
                ? (f.excludedEntityIds as number[]).map(id => entityMap.get(id) ?? `#${id}`).join(', ')
                : '—',
            isActive: f.isActive,
        })));
    }, [fetchFlags.data, countries, entities]);

    useEffect(() => {
        if (upsertResult.error) showError('Failed to save feature flag');
        else if (upsertResult.data) showSuccess('Feature flag saved');
    }, [upsertResult.data, upsertResult.error]);

    useEffect(() => {
        if (deleteResult.error) showError('Failed to delete feature flag');
        else if (deleteResult.data) showSuccess('Feature flag deleted');
    }, [deleteResult.data, deleteResult.error]);

    const handleSubmit = (formData: { featureKey: string; label: string; allowedCountryIds: number[]; excludedEntityIds: number[]; isActive: boolean }) => {
        setUpsert({ ...buildApiRequestConfig(routes.features.upsert), body: formData });
        setOpenModal('');
        setSelectedRow(null);
    };

    const handleDelete = () => {
        if (!selectedRow) return;
        setDelete(buildApiRequestConfig(routes.features.delete, [{ param: 'id', value: selectedRow.id }]));
        setOpenModal('');
        setSelectedRow(null);
    };

    const handleToggleActive = (row: FlagRow, val: boolean) => {
        setToggle({
            ...buildApiRequestConfig(routes.features.upsert),
            body: {
                featureKey: row.featureKey,
                label: row.label,
                allowedCountryIds: row.allowedCountryIds,
                excludedEntityIds: row.excludedEntityIds,
                isActive: val,
            },
        });
    };

    const columns: Column[] = [
        { id: 'featureKey', label: 'Feature Key', size: 130 },
        { id: 'label', label: 'Label', size: 120 },
        { id: 'allowedCountryNames', label: 'Allowed Countries', size: 200 },
        { id: 'excludedEntityNames', label: 'Excluded Entities', size: 200 },
        {
            id: 'isActive',
            label: 'Active',
            type: 'toggle',
            size: 80,
            onToggle: (row, val) => handleToggleActive(row as FlagRow, val),
        },
        {
            id: 'actions',
            label: 'Actions',
            type: 'actions',
            size: 80,
            onEdit: (row) => {
                setSelectedRow(row as FlagRow);
                setOpenModal('edit');
            },
            onDelete: (row) => {
                setSelectedRow(row as FlagRow);
                setOpenModal('delete');
            },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddFeatureFlagModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => { setOpenModal(''); setSelectedRow(null); }}
                onSubmit={handleSubmit}
                isEdit={openModal === 'edit'}
                editData={selectedRow}
                countries={countries}
                entities={entities}
                existingKeys={data.map(f => f.featureKey)}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Feature Flag?"
                description="This will permanently remove the feature flag and restore access to all users."
                onCancel={() => setOpenModal('')}
                image={{ src: '', width: 0, height: 0 }}
                onConfirm={handleDelete}
            />
            <Topbar title="Feature Flags" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="flex-end">
                    <AddBtn onClick={() => setOpenModal('add')} label="Add Feature Flag" />
                </Box>
                <DynamicAdminTable
                    columns={columns}
                    data={data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={Math.ceil(data.length / PAGE_SIZE)}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={data.length}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default FeatureFlags;
